import colorLib from 'color';

import type { ThemeOptions } from './createTheme';
import { getThemeShadows } from './getThemeShadows';
import logoIcon from '../icons/logo-icon-colored.svg';
import logo from '../icons/logo-white.svg';

const primary = '#11F971';
const primaryLight = '#83FCB5';
const primaryDark = '#04BB50';
const secondary = '#206bff';
const secondaryDark = '#1754CF';
const secondaryLight = '#578FFF';
const backgroundDefault = '#0e0e11';
const shadows = getThemeShadows(backgroundDefault);
const paper = '#17181b';

const fonts = {
  main: "'Roboto', sans-serif",
  secondary: "'Public Sans', sans-serif",
};

interface ThemeDark extends ThemeOptions {}

export const themeDark: ThemeDark = {
  typography: {
    fontFamily: fonts.main,
    fontFamilySecondary: fonts.secondary,
    fontWeightRegular: 500,
    fontWeightBold: 700,
    fontSize: 13,
    h1: {
      fontFamily: 'Public Sans',
      letterSpacing: '-0.5px',
    },
    h2: {
      fontFamily: 'Public Sans',
      letterSpacing: '-0.5px',
    },
    h3: {
      fontFamily: 'Public Sans',
    },
    h4: {
      fontFamily: 'Public Sans',
    },
    h5: {
      fontFamily: 'Public Sans',
    },
    h6: {
      fontFamily: 'Public Sans',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
@font-face {
  font-family: 'Public Sans';
  font-style: normal;
  font-weight: 500;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/publicsans/v13/ijwRs572Xtc6ZYQws9YVwnNGfJ7QwOk1.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
};
@font-face {
  font-family: 'Public Sans';
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/publicsans/v13/ijwRs572Xtc6ZYQws9YVwnNGfJ7QwOk1.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
};
        @font-face {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fBBc4AMP6lQ.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
};
@font-face {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfBBc4AMP6lQ.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
};
      `,
    },
  },
  palette: {
    mode: 'dark',
    divider: '#2d2f37',
    success: {
      light: colorLib('#81c46e').lighten(0.5).toString(),
      main: '#81c46e',
      dark: colorLib('#81c46e').darken(0.5).toString(),
    },
    error: {
      light: colorLib('#fc8b83').lighten(0.5).toString(),
      main: '#fc8b83',
      dark: colorLib('#fc8b83').darken(0.5).toString(),
    },
    background: {
      default: backgroundDefault,
      paper,
    },
    primary: {
      light: primaryLight,
      main: primary,
      dark: primaryDark,
    },
    text: {
      primary: '#fff',
      secondary: '#c2c6ce',
    },
    secondary: {
      light: secondaryLight,
      main: secondary,
      dark: secondaryDark,
    },
  },

  shadowsInset: shadows[1],
  icons: {
    logo,
    logoIcon,
  },
  scrollbar: {
    thumb: {
      main: '#24232c',
      hover: '#3c3b42',
    },
  },
  googleMapStyles: [
    {
      featureType: 'all',
      elementType: 'labels.text.fill',
      stylers: [
        {
          saturation: 36,
        },
        {
          color: '#1c1c23',
        },
        {
          lightness: 40,
        },
      ],
    },
    {
      featureType: 'all',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          visibility: 'on',
        },
        {
          color: '#1c1c23',
        },
        {
          lightness: 16,
        },
      ],
    },
    {
      featureType: 'all',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#2a2a33',
        },
        {
          lightness: '18',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#1c1c23',
        },
        {
          lightness: 17,
        },
        {
          weight: 1.2,
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'labels.text.fill',
      stylers: [
        {
          lightness: '27',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [
        {
          color: '#1c1c23',
        },
        {
          lightness: 20,
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry.fill',
      stylers: [
        {
          lightness: '-36',
        },
        {
          saturation: '-13',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#1c1c23',
        },
        {
          lightness: 21,
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry.fill',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          visibility: 'on',
        },
        {
          lightness: '40',
        },
        {
          weight: '1',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          visibility: 'on',
        },
        {
          lightness: '0',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#1c1c23',
        },
        {
          lightness: 17,
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#1c1c23',
        },
        {
          lightness: 29,
        },
        {
          weight: 0.3,
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry',
      stylers: [
        {
          color: '#1c1c23',
        },
        {
          lightness: 18,
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry',
      stylers: [
        {
          color: '#1c1c23',
        },
        {
          lightness: 24,
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'geometry',
      stylers: [
        {
          color: '#1c1c23',
        },
        {
          lightness: 19,
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#121218',
        },
        {
          lightness: 17,
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#121218',
        },
      ],
    },
  ],
};
